import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDialog, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { invalidateSignedDocumentQueries } from '../../utils/invalidate_signed_document_queries';
import { shouldShowEnableSigningButton } from '../site_document_details.helpers';
const CANNOT_UPDATE_CAN_SIGN = 'econsent.validationError.cannotUpdateCanSignProperty';
const cortexErrCode = (error) => {
    if (!error || typeof error !== 'object') {
        return undefined;
    }
    const record = error;
    if (typeof record.errCode === 'string') {
        return record.errCode;
    }
    const { data: nested } = record;
    if (nested && typeof nested === 'object') {
        const { errCode } = nested;
        if (typeof errCode === 'string') {
            return errCode;
        }
    }
    const { response } = record;
    if (response && typeof response === 'object') {
        const { data } = response;
        if (data && typeof data === 'object') {
            const { errCode } = data;
            if (typeof errCode === 'string') {
                return errCode;
            }
        }
    }
    return undefined;
};
/**
 * Releases a delayed-signing document for signature. Until the site user does this, the
 * participant can open the document but cannot sign it — which is the point of the
 * capability: the site keeps control until they have talked the participant through it.
 */
export const useEnableSigningAction = ({ apiClient, getApiErrorMessage, canModifyDocument = true, }) => {
    const dialog = useDialog();
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const queryClient = useQueryClient();
    const [isEnabling, setIsEnabling] = useState(false);
    const canEnableSigning = useCallback((document) => canModifyDocument && shouldShowEnableSigningButton(document), [canModifyDocument]);
    const enableSigning = useCallback(async (document) => {
        if (!document || !canEnableSigning(document)) {
            return;
        }
        const confirmed = await dialog.open({
            title: translate('label.site_document_details.enable_signing.title'),
            body: translate('prompt.site_document_details.enable_signing.confirmation'),
            actionButtons: [
                {
                    label: translate('action.site_document_details.enable_signing'),
                    dataTestId: 'enable-signing-accept',
                },
                {
                    label: translate('action.site_document_details.cancel'),
                    dataTestId: 'enable-signing-decline',
                },
            ],
        });
        if (!confirmed) {
            return;
        }
        try {
            setIsEnabling(true);
            try {
                await apiClient.put(`/v2/ec__signed_documents/${document._id}`, { ec__can_sign_document: true });
            }
            catch (error) {
                if (cortexErrCode(error) !== CANNOT_UPDATE_CAN_SIGN) {
                    addNotification({
                        title: '',
                        subtitle: getApiErrorMessage?.(error) ?? translate('error.site_document_details.enable_signing_failed'),
                        type: 'error',
                    });
                    return;
                }
            }
            addNotification({
                title: '',
                subtitle: translate('status.site_document_details.signing_enabled'),
                type: 'success',
            });
            invalidateSignedDocumentQueries(queryClient, { documentId: document._id });
        }
        finally {
            setIsEnabling(false);
        }
    }, [addNotification, apiClient, canEnableSigning, dialog, getApiErrorMessage, queryClient, translate]);
    return { canEnableSigning, enableSigning, isEnabling };
};
